/* eslint-disable no-param-reassign -- cada ilha é um registro mutável do gerenciador (estado, raiz, clique segurado) */
import { AppRouterContext } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { HeadManagerContext } from 'next/dist/shared/lib/head-manager-context.shared-runtime';
import { ImageConfigContext } from 'next/dist/shared/lib/image-config-context.shared-runtime';
import { LoadableContext } from 'next/dist/shared/lib/loadable-context.shared-runtime';
import { RouterContext } from 'next/dist/shared/lib/router-context.shared-runtime';
import { I18nContext } from 'next-i18next';
import type { ComponentType, Context, CSSProperties, ReactNode } from 'react';
import { createElement, isValidElement, useContext, useEffect, useId, useRef, useSyncExternalStore } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot, hydrateRoot } from 'react-dom/client';
import { ReactReduxContext } from 'react-redux';

import { ApprovedPageContext, ApprovedUiContext } from '@/components/approved-content/context';
import { HomeMotionSiteContext } from '@/components/motion/ativacao';
import { AuthContext } from '@/context/auth-context';
import { FeatureToggleContext } from '@/contexts/feature-toggle-context';
import { TrialSignUpContext } from '@/contexts/trial-sign-up-context';

import { HomeIntlContext, HomeVersaoContext } from './home-intl-context';

/**
 * Hidratação tardia dos blocos abaixo da 1ª tela da home internacional (decisão do dono: "hidratação parcial ou lazy de
 * blocos abaixo da dobra", sem mudar nada do que se vê).
 *
 * O servidor entrega o HTML de cada bloco inteiro, como antes (SEO, âncoras, layout, CSS do bloco no <head>), dentro de
 * um invólucro `<div data-hn-lazy="…" style="display:contents">` (sem caixa: não muda layout nem seletor nenhum dos
 * blocos). No navegador a página hidrata o invólucro VAZIO (`dangerouslySetInnerHTML` = '' com
 * `suppressHydrationWarning`: o React deixa os filhos do servidor intactos e nunca mais toca neles) e o bloco vira uma
 * "ilha": uma raiz React própria (`hydrateRoot` no invólucro) que só baixa o pedaço de JS do bloco e hidrata o HTML do
 * servidor quando:
 *   1. o bloco está na tela ao carregar (ex.: a URL abre com âncora para ele);
 *   2. depois do 1º gesto do visitante (rolagem, roda, toque, tecla, clique): todas as ilhas hidratam, uma por vez, em
 *      tempo ocioso, a mais perto da tela primeiro (`hydrateAllQueued`); e um bloco que chegue a 1,5 tela de distância
 *      antes da vez dele hidrata na hora;
 *   3. o visitante interage com ele (ponteiro por cima, toque, foco, tecla, clique);
 *   4. a URL troca de âncora (#faq…) ou um link de âncora da página é clicado para um alvo dentro dele.
 * Quando a página chega por navegação no cliente (clique no logo, link do menu) não existe HTML do servidor: o invólucro
 * nasce vazio e o bloco é renderizado na hora, do zero (`createRoot`), sem esperar gesto nem observador.
 * Quando o HTML do servidor de um bloco foi reescrito por outra coisa antes da vez dele (o tradutor do navegador troca o
 * texto por `<font>` traduzido; uma extensão mexe no DOM), hidratar não bate: o React acusa #418/#425/#423 e renderiza o
 * bloco de novo no cliente. A ilha detecta a reescrita (o texto ou a quantidade de elementos de agora difere do que ela
 * guardou ao nascer, ou a página está marcada como traduzida) e renderiza o bloco do zero sem tentar hidratar: o resultado
 * na tela é o mesmo, sem os erros (SCRUM-2593). Mudança só de atributo não conta: a hidratação não a compara, e a própria
 * página põe atributos nos blocos antes da vez deles (`data-hn-cv-off`, use-content-visibility-release.ts). O tradutor
 * volta a traduzir o texto novo, como faz com qualquer conteúdo que muda.
 * Um clique num bloco ainda não hidratado é segurado e repetido no mesmo elemento logo depois da hidratação (os botões
 * do bloco funcionam no 1º clique); se o pedaço não carregar, o clique segue sem o React (links continuam navegando).
 *
 * Por que raízes separadas e não `<Suspense>` + `lazy` (hidratação seletiva do React 18): uma fronteira de Suspense ainda
 * desidratada que recebe QUALQUER mudança de contexto vinda de cima (o `AuthProvider` do _app gera um valor novo a cada
 * render do _app, que re-renderiza sozinho depois do load) é descartada pelo React e renderizada do zero no cliente: o
 * HTML do servidor sumiria até o pedaço chegar. A ilha não está na árvore da página: nenhuma atualização de cima a
 * alcança. Os contextos que os blocos leem (roteador, imagens, i18n, teste grátis, par da página…) passam por uma ponte
 * (`BRIDGED`): a ilha hidrata com os valores da hidratação da página (os mesmos do servidor, então o HTML bate) e recebe
 * os valores atuais logo depois, como a página receberia.
 *
 * Bloco novo dentro de uma ilha que leia OUTRO contexto: ponha o contexto em `BRIDGED` (sem ele, o bloco lê o valor
 * padrão e a hidratação da ilha falha com erro no console).
 *
 * Sair da página enquanto uma ilha ainda hidrata (a hidratação de um bloco grande vai em fatias, e o visitante pode
 * clicar num link do header no meio dela) desmonta uma raiz que ainda não adotou o HTML do servidor. O React trata a
 * desmontagem como uma atualização que chegou antes da hidratação e avisa com o erro #424 ("This root received an early
 * update"), sem efeito nenhum para o visitante: a raiz está sendo jogada fora. O que a raiz avisa durante a própria
 * desmontagem (na prática, só esse erro) é descartado no `onRecoverableError`; os erros recuperáveis da ilha antes disso
 * seguem para o `reportError`, como no padrão do React (SCRUM-2595).
 */

// Contextos que atravessam para as ilhas. A ordem é fixa (um useContext por item, sempre na mesma ordem).
// ApprovedPageContext: a página aprovada do par (links e textos do cabeçalho/rodapé, teste grátis), lida pelos blocos da
// página de Download nova (src/components/download-nova) nas ilhas; fora de uma página aprovada o valor é null dos dois lados.
// ApprovedUiContext: os rótulos aprovados da interface do par, lidos pelas seções das páginas de recursos e pelas animações
// delas (ilhas isoladas, src/components/recursos/modelo); fora delas o valor é null dos dois lados.
// HomeVersaoContext e HomeMotionSiteContext: a versão (v2–v4) e o motion da Home nos locales do site, sem par (o preview).
// (O filtro só tira um contexto que falte num teste com o módulo dele simulado.)
const BRIDGED: Context<unknown>[] = ([RouterContext, AppRouterContext, ImageConfigContext, HeadManagerContext, I18nContext, ReactReduxContext, AuthContext, FeatureToggleContext, TrialSignUpContext, HomeIntlContext, HomeVersaoContext, HomeMotionSiteContext, ApprovedPageContext, ApprovedUiContext] as unknown as (Context<unknown> | undefined)[]).filter((context): context is Context<unknown> => Boolean(context));

/** Margem do "perto da tela" depois do 1º gesto: 1,5 tela acima e abaixo. */
export const HOME_NOVA_LAZY_NEAR_MARGIN = '150% 0px 150% 0px';

const WRAPPER_STYLE: CSSProperties = { display: 'contents' };
const EMPTY_HTML = { __html: '' };

type Values = unknown[];

interface Island {
  name: string;
  el: HTMLElement;
  load: () => Promise<ComponentType<object>>;
  /** Props atuais (só a ilha isolada as atualiza; ver `lazyHydratedIsolated`). */
  props: object;
  /** Props da hidratação da página (= servidor). */
  initialProps: object;
  /** A raiz está sendo desmontada (a página saiu): o aviso de atualização antes da hidratação não vale nada. */
  unmounting?: boolean;
  /** Só a ilha isolada: prefixo dos ids do useId() da raiz dela (o mesmo do servidor). */
  identifierPrefix?: string;
  /** Forma do HTML do servidor (texto e quantidade de elementos) quando a ilha nasce; se mudou até a vez dela, alguém reescreveu o bloco (ver `wasRewritten`). */
  serverShape?: string;
  /** Valores dos contextos na hidratação da página (= servidor). */
  initial: Values;
  /** Valores atuais (a ponte atualiza). */
  current: Values;
  listeners: Set<() => void>;
  state: 'idle' | 'loading' | 'hydrated' | 'failed';
  module?: Promise<ComponentType<object>>;
  root?: Root;
  pendingClick?: { target: Element; init: MouseEventInit };
  cleanup: () => void;
  /** Avisa quem espera a hidratação desta ilha (fila do 1º gesto). */
  settled: Set<() => void>;
}

const islands = new Set<Island>();
let engaged = false;
let observer: IntersectionObserver | null = null;
let managerReady = false;
let queueStarted = false;

const sameValues = (a: Values, b: Values) => a.length === b.length && a.every((value, index) => Object.is(value, b[index]));

/** Até onde as props de uma ilha isolada são comparadas (parágrafos → itens → blocos aprovados…); mais fundo conta como diferente. */
const PROPS_DEPTH = 8;

/**
 * Mesmo conteúdo? A página recria a cada render os arrays e os elementos que passa às ilhas (ex.: `animacao={<X />}`):
 * compara tipo, chave e props dos elementos e o conteúdo dos arrays e objetos simples, não a identidade.
 */
function sameValue(a: unknown, b: unknown, depth: number): boolean {
  if (Object.is(a, b)) return true;
  if (depth <= 0 || !a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (isValidElement(a) || isValidElement(b)) return isValidElement(a) && isValidElement(b) && a.type === b.type && a.key === b.key && sameValue(a.props, b.props, depth - 1);
  if (Array.isArray(a) || Array.isArray(b)) return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((item, index) => sameValue(item, b[index], depth - 1));
  if (Object.getPrototypeOf(a) !== Object.prototype || Object.getPrototypeOf(b) !== Object.prototype) return false;
  const recordA = a as Record<string, unknown>;
  const recordB = b as Record<string, unknown>;
  const keys = Object.keys(recordA);
  return keys.length === Object.keys(recordB).length && keys.every((key) => Object.prototype.hasOwnProperty.call(recordB, key) && sameValue(recordA[key], recordB[key], depth - 1));
}

const sameProps = (a: object, b: object) => sameValue(a, b, PROPS_DEPTH);

function Bridge({ values, children }: { values: Values; children: ReactNode }) {
  return BRIDGED.reduceRight<ReactNode>((acc, context, index) => createElement(context.Provider, { value: values[index] }, acc), children);
}

function IslandRoot({ island, Component }: { island: Island; Component: ComponentType<object> }) {
  // Na hidratação, os valores da hidratação da página (o HTML do servidor foi feito com eles); depois, os atuais.
  const values = useSyncExternalStore(
    (onChange) => {
      island.listeners.add(onChange);
      return () => island.listeners.delete(onChange);
    },
    () => island.current,
    () => island.initial
  );
  const props = useSyncExternalStore(
    (onChange) => {
      island.listeners.add(onChange);
      return () => island.listeners.delete(onChange);
    },
    () => island.props,
    () => island.initialProps
  );
  useEffect(() => {
    island.state = 'hydrated';
    island.settled.forEach((done) => done());
    island.settled.clear();
    const click = island.pendingClick;
    island.pendingClick = undefined;
    // o clique segurado antes da hidratação: repetido no mesmo elemento, agora com os handlers do bloco
    if (click && click.target.isConnected) click.target.dispatchEvent(new MouseEvent('click', click.init));
  }, [island]);
  return <Bridge values={values}>{createElement(Component, props)}</Bridge>;
}

/** Classes que o tradutor do Chrome e do Google põem no <html> de uma página traduzida. */
const TRANSLATED_CLASSES = ['translated-ltr', 'translated-rtl'];

/** O que a hidratação compara: o texto e os elementos do bloco (atributos ficam de fora). */
const shapeOf = (el: HTMLElement) => `${el.getElementsByTagName('*').length}:${el.textContent ?? ''}`;

/** O HTML do servidor do bloco foi reescrito antes da hidratação (tradutor do navegador, extensão)? */
function wasRewritten(island: Island) {
  if (TRANSLATED_CLASSES.some((name) => document.documentElement.classList.contains(name))) return true;
  return island.serverShape !== undefined && shapeOf(island.el) !== island.serverShape;
}

function loadModule(island: Island) {
  if (!island.module) {
    island.module = island.load();
    island.module.catch(() => undefined);
  }
  return island.module;
}

/** O que o React 18 faz com um erro recuperável quando a raiz não informa `onRecoverableError`. */
function reportRecoverableError(error: unknown) {
  if (typeof reportError === 'function') reportError(error);
  // eslint-disable-next-line no-console -- o mesmo destino do padrão do React em navegador sem reportError
  else console.error(error);
}

function hydrateIsland(island: Island) {
  if (island.state !== 'idle') return;
  island.state = 'loading';
  loadModule(island).then(
    (Component) => {
      if (!islands.has(island)) return;
      observer?.unobserve(island.el);
      Array.from(island.el.children).forEach((child) => observer?.unobserve(child));
      const node = <IslandRoot island={island} Component={Component} />;
      const options = island.identifierPrefix ? { identifierPrefix: island.identifierPrefix } : undefined;
      const rewritten = island.el.hasChildNodes() && wasRewritten(island);
      island.serverShape = undefined;
      if (island.el.hasChildNodes() && !rewritten) {
        island.root = hydrateRoot(island.el, node, {
          ...options,
          onRecoverableError: (error) => {
            if (!island.unmounting) reportRecoverableError(error);
          },
        });
        return;
      }
      // navegação no cliente (ex.: clique no logo): o invólucro chega vazio, não há HTML do servidor para hidratar;
      // HTML reescrito antes da hidratação (tradutor, extensão): hidratar falharia, então o bloco é renderizado do zero
      if (rewritten) island.el.replaceChildren();
      island.root = createRoot(island.el, options);
      island.root.render(node);
    },
    () => {
      // sem o pedaço (rede caiu): o HTML do servidor fica como está; um clique segurado segue sem o React
      island.state = 'failed';
      island.settled.forEach((done) => done());
      island.settled.clear();
      const click = island.pendingClick;
      island.pendingClick = undefined;
      if (click && click.target.isConnected) click.target.dispatchEvent(new MouseEvent('click', click.init));
    }
  );
}

function islandOf(node: Node | null): Island | undefined {
  if (!node) return undefined;
  for (const island of islands) if (island.el.contains(node)) return island;
  return undefined;
}

function hydrateTarget(hash: string) {
  if (!hash || hash === '#') return;
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    // âncora malformada: usa como veio
  }
  const target = document.getElementById(id);
  const island = islandOf(target);
  if (island) hydrateIsland(island);
}

const hasObserver = () => typeof IntersectionObserver !== 'undefined';

function observeIsland(island: Island) {
  // navegador sem IntersectionObserver: hidrata já (melhor que um bloco que nunca responde)
  if (!hasObserver()) {
    hydrateIsland(island);
    return;
  }
  if (!observer || island.state !== 'idle') return;
  // o invólucro (display: contents) não tem caixa: observa os filhos
  Array.from(island.el.children).forEach((child) => observer?.observe(child));
}

function createObserver() {
  if (!hasObserver()) return;
  observer?.disconnect();
  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        // só encostar na borda não conta (no computador o bloco seguinte ao hero começa exatamente na dobra)
        if (!entry.isIntersecting || entry.intersectionRect.height <= 0) return;
        const island = islandOf(entry.target);
        if (island) hydrateIsland(island);
      });
    },
    { rootMargin: engaged ? HOME_NOVA_LAZY_NEAR_MARGIN : '0px' }
  );
  islands.forEach(observeIsland);
}

const idle = (fn: () => void) => {
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (w.requestIdleCallback) w.requestIdleCallback(fn, { timeout: 1000 });
  else setTimeout(fn, 50);
};

/** Distância (px) do bloco até a tela; 0 se já está nela. */
function distanceToViewport(island: Island) {
  const rects = Array.from(island.el.children).map((child) => child.getBoundingClientRect());
  if (!rects.length) return Infinity;
  const top = Math.min(...rects.map((rect) => rect.top));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));
  if (bottom < 0) return -bottom;
  if (top > window.innerHeight) return top - window.innerHeight;
  return 0;
}

const settledOf = (island: Island) =>
  new Promise<void>((resolve) => {
    if (island.state === 'hydrated' || island.state === 'failed') resolve();
    else island.settled.add(resolve);
  });

/**
 * Depois do 1º gesto: baixa os pedaços de todos os blocos (em paralelo) e hidrata TODAS as ilhas, uma por vez, em tempo
 * ocioso, a mais perto da tela primeiro (a ordem é refeita a cada passo: o visitante pode estar rolando). Assim, quando
 * ele chega a qualquer bloco — rolando, pelo menu ou por âncora — o bloco já está hidratado havia tempo e as animações de
 * entrada (contagem dos números etc.) acontecem como na página toda hidratada ao carregar. O Lighthouse não faz gesto.
 */
function hydrateAllQueued() {
  if (queueStarted) return;
  queueStarted = true;
  islands.forEach((island) => loadModule(island));
  const next = () => {
    const pending = Array.from(islands).filter((island) => island.state === 'idle');
    if (!pending.length) return;
    pending.sort((first, second) => distanceToViewport(first) - distanceToViewport(second));
    const island = pending[0] as Island;
    hydrateIsland(island);
    settledOf(island).then(() => idle(next));
  };
  idle(next);
}

const ENGAGE_EVENTS = ['scroll', 'wheel', 'touchstart', 'pointerdown', 'keydown'] as const;

function engage() {
  if (engaged) return;
  engaged = true;
  ENGAGE_EVENTS.forEach((type) => window.removeEventListener(type, engage, true));
  createObserver();
  hydrateAllQueued();
}

function setUpManager() {
  if (managerReady) return;
  managerReady = true;
  ENGAGE_EVENTS.forEach((type) => window.addEventListener(type, engage, { capture: true, passive: true }));
  window.addEventListener('hashchange', () => hydrateTarget(window.location.hash));
  // link de âncora da própria página: o bloco de destino começa a hidratar já no clique (a rolagem é suave)
  document.addEventListener(
    'click',
    (event) => {
      const link = (event.target as Element | null)?.closest?.('a[href*="#"]') as HTMLAnchorElement | null;
      if (!link || link.origin !== window.location.origin || link.pathname !== window.location.pathname) return;
      hydrateTarget(link.hash);
    },
    true
  );
  createObserver();
}

const INTERACTION_EVENTS = ['pointerover', 'pointerdown', 'touchstart', 'focusin', 'keydown'] as const;

function registerIsland(island: Island) {
  islands.add(island);
  const onInteract = () => hydrateIsland(island);
  // clique antes da hidratação: segura e repete depois (sem isso os botões do bloco não responderiam a ele)
  const onClick = (event: MouseEvent) => {
    if (island.state === 'hydrated' || island.state === 'failed' || !event.isTrusted || !(event.target instanceof Element)) return;
    event.preventDefault();
    event.stopPropagation();
    island.pendingClick = {
      target: event.target,
      init: { bubbles: true, cancelable: true, composed: true, view: window, detail: event.detail, screenX: event.screenX, screenY: event.screenY, clientX: event.clientX, clientY: event.clientY, ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey, metaKey: event.metaKey, button: event.button, buttons: event.buttons },
    };
    hydrateIsland(island);
  };
  INTERACTION_EVENTS.forEach((type) => island.el.addEventListener(type, onInteract, { capture: true, passive: true }));
  island.el.addEventListener('click', onClick, true);
  island.cleanup = () => {
    INTERACTION_EVENTS.forEach((type) => island.el.removeEventListener(type, onInteract, true));
    island.el.removeEventListener('click', onClick, true);
    Array.from(island.el.children).forEach((child) => observer?.unobserve(child));
  };
  setUpManager();
  // sem HTML do servidor (a página chegou por navegação no cliente) não há o que esperar: o bloco é renderizado já,
  // senão ficaria vazio para sempre (o observador olha os filhos do invólucro e a fila do 1º gesto só roda uma vez)
  if (!island.el.hasChildNodes()) {
    hydrateIsland(island);
    return;
  }
  // a forma do HTML do servidor como chegou: se mudar até a vez da ilha, ela não tenta hidratar (ver `wasRewritten`)
  island.serverShape = shapeOf(island.el);
  observeIsland(island);
  // a URL abriu com âncora para dentro do bloco
  if (window.location.hash) hydrateTarget(window.location.hash);
}

function IslandHost({ name, load, props, identifierPrefix }: { name: string; load: () => Promise<ComponentType<object>>; props: object; identifierPrefix?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line react-hooks/rules-of-hooks -- BRIDGED tem tamanho e ordem fixos
  const values = BRIDGED.map((context) => useContext(context));
  const islandRef = useRef<Island | null>(null);
  const initialRef = useRef<Values>(values);
  const initialPropsRef = useRef<object>(props);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const island: Island = { name, el, load, props: initialPropsRef.current, initialProps: initialPropsRef.current, identifierPrefix, initial: initialRef.current, current: initialRef.current, listeners: new Set(), state: 'idle', cleanup: () => undefined, settled: new Set() };
    islandRef.current = island;
    registerIsland(island);
    return () => {
      island.cleanup();
      islands.delete(island);
      island.settled.forEach((done) => done());
      island.settled.clear();
      islandRef.current = null;
      const { root } = island;
      // desmontar outra raiz durante o commit desta não é permitido: fica para depois
      if (root) {
        setTimeout(() => {
          island.unmounting = true;
          root.unmount();
        }, 0);
      }
    };
    // a ilha nasce uma vez por montagem do invólucro
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ponte: valores novos dos contextos chegam à ilha (antes de hidratar só ficam guardados); na ilha isolada, as props novas
  // também (a mesma página com outro par, por navegação no cliente, reaproveita a ilha)
  useEffect(() => {
    const island = islandRef.current;
    if (!island) return;
    const hasNewValues = !sameValues(island.current, values);
    const hasNewProps = Boolean(identifierPrefix) && !sameProps(island.props, props);
    if (!hasNewValues && !hasNewProps) return;
    if (hasNewValues) island.current = values;
    if (hasNewProps) island.props = props;
    island.listeners.forEach((listener) => listener());
  });

  // innerHTML vazio e fixo: o React nunca escreve nele (os filhos são o HTML do servidor, hidratado pela ilha)
  // eslint-disable-next-line react/no-danger
  return <div ref={ref} data-hn-lazy={name} style={WRAPPER_STYLE} dangerouslySetInnerHTML={EMPTY_HTML} suppressHydrationWarning />;
}

/**
 * Bloco com hidratação tardia.
 * @param name   nome do bloco (atributo `data-hn-lazy`)
 * @param load   no navegador: o `import()` do bloco (mesmo pedido do `server`, então a mesma entrada do
 *               react-loadable-manifest: o CSS do bloco continua no <head> e `_document` tira só o JS — ver
 *               `src/utils/home-nova-lazy-scripts.ts`)
 * @param server só no servidor: o bloco por `dynamic()` (null no navegador, onde o `dynamic()` não pode existir — se
 *               existisse, o Next baixaria e carregaria o pedaço antes de hidratar a página)
 */
export function lazyHydrated<P extends object>(name: string, load: () => Promise<ComponentType<P>>, server: ComponentType<P> | null): ComponentType<P> {
  function LazyHydrated(props: P) {
    if (server) {
      return (
        <div data-hn-lazy={name} style={WRAPPER_STYLE}>
          {createElement(server, props)}
        </div>
      );
    }
    return <IslandHost name={name} load={load as () => Promise<ComponentType<object>>} props={props} />;
  }
  LazyHydrated.displayName = `LazyHydrated(${name})`;
  return LazyHydrated;
}

type RenderToString = (element: ReactNode, options?: { identifierPrefix?: string }) => string;

/**
 * Só no servidor: o `renderToString` que gera o HTML de uma ilha isolada numa raiz própria. No navegador fica null e o
 * `require` não entra no pacote (o build do cliente troca `typeof window` por constante e corta o ramo).
 */
const renderIsland: RenderToString | null =
  typeof window === 'undefined'
    ? // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires, @typescript-eslint/no-require-imports -- só no servidor (ver acima)
      (require('react-dom/server') as { renderToString: RenderToString }).renderToString
    : null;

function IsolatedServerIsland({ name, Component, props, identifierPrefix }: { name: string; Component: ComponentType<object>; props: object; identifierPrefix: string }) {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- BRIDGED tem tamanho e ordem fixos
  const values = BRIDGED.map((context) => useContext(context));
  // o next/dynamic de dentro da ilha (se houver) continua registrando o pedaço dele (JS e CSS) no HTML da página
  const capture = useContext(LoadableContext);
  const html = (renderIsland as RenderToString)(createElement(LoadableContext.Provider, { value: capture }, <Bridge values={values}>{createElement(Component, props)}</Bridge>), { identifierPrefix });
  // HTML gerado pelo próprio React (o texto já sai escapado), não por texto de fora
  // eslint-disable-next-line react/no-danger
  return <div data-hn-lazy={name} style={WRAPPER_STYLE} dangerouslySetInnerHTML={{ __html: html }} />;
}

/**
 * Bloco com hidratação tardia e IDS ISOLADOS: a variante das páginas de conteúdo (seções das páginas de recursos, FAQ e
 * rodapé delas — src/components/recursos/modelo). Mesmas regras de quando hidrata do `lazyHydrated` (na tela ao carregar,
 * 1º gesto → todas em fila ociosa, a mais perto primeiro; interação; âncora), com duas diferenças:
 *
 * 1. O servidor renderiza o bloco numa raiz React PRÓPRIA (`renderToString`, com os contextos da ponte), não dentro da
 *    árvore da página. Assim os ids do `useId()` de dentro do bloco (gradientes de SVG, `aria-labelledby`, as faixas de
 *    reserva do `sobDemanda`) são os mesmos que a raiz da ilha calcula no navegador: com o bloco na árvore da página eles
 *    dependeriam da posição dele lá e a hidratação da ilha não bateria. Cada ilha tem um prefixo próprio
 *    (`identifierPrefix`, tirado do `useId()` do invólucro, igual nos dois lados): os ids de duas ilhas nunca colidem.
 * 2. O bloco vem do próprio pacote da página (sem `import()` nem pedaço próprio): adia-se a hidratação, não o download.
 *    As props novas da página (outro par por navegação no cliente) chegam à ilha.
 *
 * Contextos que o bloco lê: os de `BRIDGED` (fora deles, o bloco lê o valor padrão e a hidratação falha).
 */
export function lazyHydratedIsolated<P extends object>(name: string, Component: ComponentType<P>): ComponentType<P> {
  const component = Component as unknown as ComponentType<object>;
  const load = () => Promise.resolve(component);
  function LazyHydratedIsolated(props: P) {
    const identifierPrefix = `np${useId().replace(/[^a-zA-Z0-9]/g, '')}-`;
    if (renderIsland) return <IsolatedServerIsland name={name} Component={component} props={props} identifierPrefix={identifierPrefix} />;
    return <IslandHost name={name} load={load} props={props} identifierPrefix={identifierPrefix} />;
  }
  LazyHydratedIsolated.displayName = `LazyHydratedIsolated(${name})`;
  return LazyHydratedIsolated;
}
